import { useCallback, useEffect, useMemo, useState } from 'react';
import { PaginationState } from '../TanstackTable/types';
import { DEFAULT_INITIAL_PAGE_SIZE } from '../TanstackTable/constants';
import { useAutoPageSize, UseAutoPageSizeOptions } from './useAutoPageSize';
import {
  nextPagination,
  PaginatedTableProps,
  PaginationResetOptions,
  useResetOnChange,
} from './pagination';

export interface UseClientPaginationOptions extends UseAutoPageSizeOptions, PaginationResetOptions {
  /** Page size before the first measurement lands. */
  initialPageSize?: number;
}

export interface UseClientPaginationResult {
  pagination: PaginationState;
  /** Jump back to the first page. Wired to `resetOn`, and callable directly. */
  resetPage: () => void;
  /** Spread onto a client-mode `<TanstackTable>`. */
  tableProps: PaginatedTableProps;
}

/**
 * Auto-fitting pagination for a client-mode table: everything
 * `useAutoPageSize` measures, applied straight away.
 *
 * Re-paginating client-side costs nothing, so a fitted size is applied the
 * moment it is measured — no debounce, no quantisation, an exact fit.
 *
 * ```tsx
 * const pagination = useClientPagination({ pageSizeOptions: [5, 10, 25, 50] });
 * <TanstackTable mode="client" data={rows} columns={columns} {...pagination.tableProps} />
 * ```
 */
export function useClientPagination({
  initialPageSize = DEFAULT_INITIAL_PAGE_SIZE,
  resetOn,
  ...autoPageSizeOptions
}: UseClientPaginationOptions = {}): UseClientPaginationResult {
  const autoPageSize = useAutoPageSize(autoPageSizeOptions);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  const fittedPageSize = autoPageSize.pageSize;

  // Keyed on the current size as well as the fitted one, so the fit is restored
  // rather than lost for good if anything else writes pagination.
  useEffect(() => {
    if (fittedPageSize === null || pagination.pageSize === fittedPageSize) return;
    setPagination((previous: PaginationState) => {
      if (previous.pageSize === fittedPageSize) return previous;
      // Keep the row currently at the top of the page at the top of the new one,
      // so resizing browses the data instead of jumping through it.
      const anchorRow = previous.pageIndex * previous.pageSize;
      return {
        pageIndex: Math.floor(anchorRow / fittedPageSize),
        pageSize: fittedPageSize,
      };
    });
  }, [fittedPageSize, pagination.pageSize]);

  const onPaginationChange = useCallback((next: PaginationState): void => {
    setPagination((previous: PaginationState) => nextPagination(previous, next));
  }, []);

  const resetPage = useCallback((): void => {
    setPagination((previous: PaginationState) =>
      previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }
    );
  }, []);

  useResetOnChange(resetOn, resetPage);

  // Memoised so the table is not handed a fresh props object on every render of
  // the page — which would also waste the memo `useAutoPageSize` already does.
  const tableProps = useMemo<PaginatedTableProps>(
    () => ({ ...autoPageSize.tableProps, pagination, onPaginationChange }),
    [autoPageSize.tableProps, pagination, onPaginationChange]
  );

  return {
    pagination,
    resetPage,
    tableProps,
  };
}

export default useClientPagination;
